import { RefreshFlag } from "@/refresh_flag.tsx"
import { api_fetch_throws, BackupViewResponse, DeductionDetailJson } from "@/api"
import { DeductionHeaderLeft, DeductionHeaderRight } from "@/accounting/accounting_transaction.tsx"
import { StatusSelect } from "@/deduction_table/status_select.tsx"
import { DeductionDetailActions, DeductionDetailsSection } from "@/deduction_detail"
import { UserFiles } from "@/deduction/[id]/UserFiles.tsx"
import { Timeline2 } from "@/deduction_detail/Timeline2"
import { RightPane } from "@/deduction_detail/RightPane"
import { ChecklistSection } from "@/deduction_detail/ChecklistSection"
import { BackupSection } from "@/deduction_detail/BackupSection.tsx"
import { DisputePlaybookPanel } from "@/deduction_detail/DisputePlaybookPanel.tsx"
import { ContractRateCheckCallout } from "@/deduction_detail/ContractRateCheckCallout.tsx"
import { FillRateFineReviewPanel } from "@/deduction_detail/FillRateFineReviewPanel.tsx"
import { PurchaseOrderPanel } from "@/deduction_detail/PurchaseOrderPanel.tsx"
import { useFetch } from "@/utils"
import { useRef, useCallback, useState } from "preact/hooks"
import { useWizard } from "@/deduction_detail/Triage2"
import { useSearch } from "wouter-preact/use-location"
import { DrawerDeduction } from "@/dispute/file_dispute_drawer.tsx"
import { AssignRepaymentDialog } from "@/deduction_detail/AssignRepaymentDialog"

interface DeductionDetailContentProps {
  deduction: DeductionDetailJson
  onUpdate: (updated: DeductionDetailJson) => void
}

export function DeductionDetailContent({ deduction, onUpdate }: DeductionDetailContentProps) {
  const backupTableRef = useRef<HTMLDivElement>(null)
  const refreshFlag = RefreshFlag.use()

  const searchString = useSearch()
  const searchParams = new URLSearchParams(searchString)
  const autoOpenWizard = searchParams.get("wizard") === "true"

  const wizard = useWizard({ deduction, autoOpen: autoOpenWizard })

  const [repaymentDialogOpen, setRepaymentDialogOpen] = useState(false)

  const handleFileDispute = useCallback(() => {
    DrawerDeduction.set(deduction)
  }, [deduction])

  const handleMatchOriginal = useCallback(() => {
    setRepaymentDialogOpen(true)
  }, [])

  function handleParseComplete(fileKind: string) {
    // If a backup file was parsed, scroll to the backup table
    if (fileKind === "backup" && backupTableRef.current) {
      // Small delay to allow the backup data to refresh
      setTimeout(() => {
        backupTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
      }, 500)
    }
  }

  let { data: backup } = useFetch(
    () => api_fetch_throws<BackupViewResponse>(`/deduction/${deduction.id}/backup?include_raw=true`),
    [deduction.id, refreshFlag],
    { rows: [], dynamic_table: null }
  )

  return (
    <div class="w-full">
      <div class="flex items-end justify-between my-3 mx-4">
        <DeductionHeaderLeft deduction={deduction}>
          {["deduction", "invoice"].includes(deduction.kind) && deduction.kind !== "repayment" && (
            <StatusSelect deduction={deduction} onStatusChange={() => RefreshFlag.set(f => !f)} />
          )}
        </DeductionHeaderLeft>
        <DeductionHeaderRight deduction={deduction}>
          <DeductionDetailActions deduction={deduction} onTriggerWizard={wizard.trigger} />
        </DeductionHeaderRight>
      </div>
      <div class="flex gap-8 mt-4 mx-4">
        <div class="flex flex-col gap-6 max-w-1/3">
          {deduction.kind !== "repayment" && <ChecklistSection deduction={deduction} />}
          <DeductionDetailsSection
            deduction={deduction}
            onUpdate={onUpdate}
            className="min-w-36 lg:min-w-72 xl:min-w-96"
          />
          {deduction.dispute_playbook && deduction.kind !== "repayment" && (
            <DisputePlaybookPanel playbook={deduction.dispute_playbook} />
          )}
          {deduction.contract_rate_check && deduction.kind !== "repayment" && (
            <ContractRateCheckCallout check={deduction.contract_rate_check} />
          )}
          {deduction.fill_rate_fine_review && <FillRateFineReviewPanel review={deduction.fill_rate_fine_review} />}
          {deduction.purchase_orders && deduction.purchase_orders.length > 0 && <PurchaseOrderPanel orders={deduction.purchase_orders} />}
          <UserFiles deduction={deduction} onParseComplete={handleParseComplete} />
          <Timeline2 deduction={deduction} />
        </div>
        <RightPane deduction={deduction} className="w-full" onTriggerWizard={wizard.trigger} onFileDispute={handleFileDispute} onMatchOriginal={handleMatchOriginal} />
      </div>
      <BackupSection deduction={deduction} backupResponse={backup} backupTableRef={backupTableRef} />
      {wizard.element}
      <AssignRepaymentDialog deduction={deduction} open={repaymentDialogOpen} onOpenChange={setRepaymentDialogOpen} />
    </div>
  )
}
